/**
 * Reusable "Pedir ajuda da Claire" button.
 *
 * Fires a `claire:open` CustomEvent that ClaireWidget listens for. The
 * widget opens, pre-fills the composer with `question`, and (if
 * `autoSend` is true) immediately submits it.
 *
 * Use anywhere the user might need contextual help without leaving the
 * current screen. Renders nothing for anonymous visitors — the widget
 * itself is auth-gated.
 *
 * Props:
 *   - question : string  – the pre-filled question text
 *   - autoSend : boolean – if true, send immediately on click
 *   - variant  : 'pill' | 'link' – visual style (default 'pill')
 *   - label    : string  – button label (defaults to "Pedir ajuda da Claire")
 *   - testid   : string  – custom data-testid suffix
 */
import { Sparkles } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export default function ClaireHelpButton({
  question,
  autoSend = false,
  variant = "pill",
  label = "Pedir ajuda da Claire",
  testid = "default",
}) {
  const { user } = useAuth();
  if (!user) return null;

  const onClick = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("claire:open", {
          detail: { prefill: question || "", autoSend: !!autoSend },
        }),
      );
      // Fire-and-forget telemetry — backend captures click events.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/claire/event`;
      fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "help_button_click",
          page: window.location.pathname,
          question: question || "",
          extra: { testid, autoSend: !!autoSend },
        }),
      }).catch(() => { /* ignore */ });
    } catch (_e) {
      /* no-op — old browsers without CustomEvent constructor */
    }
  };

  if (variant === "link") {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={`claire-help-link-${testid}`}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#7C3AED] hover:text-[#4F46E5] hover:underline transition-colors"
      >
        <Sparkles className="w-3 h-3" />
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`claire-help-btn-${testid}`}
      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12.5px] font-semibold text-white bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] hover:from-[#6D28D9] hover:to-[#4338CA] shadow-sm transition-colors"
      title={question ? `Claire: ${question}` : label}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
